import Reflux from "reflux";

import AppConfig from "../config/app";
import AppConstants from "../constants/AppConstants";
import { getSiteData, getSiteId, getSiteState } from "../hooks/query/useSite";
import ActiveContainer from "./ActiveBlockContainerStore.mjs";
import SiteActions from "../actions/SiteActions";
import UIActions from "../actions/UIActions";
import SiteService from "../service/SiteService.mjs";
import APIError from "../service/APIError";
import ErrorUtils from "../utils/ErrorUtils";
import DesignSaveStore from "./DesignSaveStore";
import SitePublishActions from "../actions/SitePublishActions";
import { RendererType, V1, V2 } from "@/constants/RendererConstants.mjs";
import { queryClient } from "@/utils/QueryUtils";
import { RENDERER_LOCK_ELIGIBILITY } from "@/constants/QueryKeys";

/**
 * @typedef {import("@/entities/BlockContainer").default} BlockContainer
 * @typedef {import("@/hooks/query/useSite").SiteState} SiteState
 * @typedef {import("@/types/TypeUtils.mjs").ImmutableMapFrom<SiteState>} ImmutableSiteState
 * @typedef {typeof import("@/constants/RendererConstants.mjs").RendererType} RendererType
 */

/**
 * @typedef {Object} PreviewError
 * @prop {number} code
 * @prop {string} debug
 * @prop {string} message
 * @prop {Object=} data - Not sure where this comes from. Maybe blog preview?
 */

/**
 * @typedef {Record<keyof RendererType, string>} LastPreviewState
 */

/**
 * @typedef {Object} PreviewSettings
 * @prop {string=} blogPreviewUrl
 * @prop {LastPreviewState} lastPreviewed
 * @prop {string} previewURL
 * @prop {string} pageURL
 * @prop {Object} process
 * @prop {string} process.view
 * @prop {PreviewError=} process.error
 * @prop {string} selectedLayout
 * @prop {boolean} useProtorenderer
 */

/**
 * Extra config for the lambda. Reconfigures lambda, version, and output previewId.
 * @typedef {Object} LambdaConfig
 * @prop {string=} lambdaName
 * @prop {number=} lambdaVersion
 * @prop {string=} previewId
 * @prop {boolean=} protorenderer - temporary
 */

/**
 * This is store is used by the Preview Panel
 */

const PreviewStore = Reflux.createStore({
  storeName: "PreviewStore",
  listenables: [SiteActions],

  init() {
    this.listenTo(UIActions.previewOpen, this.onPreviewOpen);
    this.listenTo(UIActions.previewClose, this.onPreviewClose);
    this.listenTo(
      SitePublishActions.generateBlogPreview,
      this.onGenerateBlogPreview,
    );
    this.listenTo(UIActions.toggleProtorenderer, this.onToggleProtorenderer);

    /** @type {PreviewSettings} */
    this.settings = {};
    /**
     * @type {LambdaConfig} - Stored after open so it isn't wiped on protorenderer toggle. Likely temporary.
     */
    this.lambdaConfig = {};

    this.designSaveListener = null;
    this.activeContainerListener = null;

    this.canGeneratePreview = false;
    this.generatingPreview = false;
    this.previewOpen = false;
    this.lastSyncStatusChange = null;
  },

  /**
   * Replaces `settings` with a new object before triggering. Reference-equality
   * subscribers (e.g. useStoreState) bail out on identical references, so
   * mutating the existing settings object in place would never re-render them.
   * @param {Partial<PreviewSettings>} patch
   */
  _updateSettings(patch) {
    this.settings = { ...this.settings, ...patch };
    this.trigger(this.settings);
  },

  // invalidate blog preview cache when user switches designs
  onLoadSite() {
    if (this.settings.blogPreviewUrl) {
      this._updateSettings({ blogPreviewUrl: null });
    }
  },

  /**
   *
   * @param {boolean=} inSync
   * @param {LambdaConfig=} lambdaConfig
   */
  _onSaveStateChange(inSync, lambdaConfig) {
    if (this.canGeneratePreview === false && inSync) {
      this.canGeneratePreview = true;

      this.lastSyncStatusChange = DesignSaveStore.getLastStatusChange();

      const site = getSiteState();
      this._generatePreview(site, lambdaConfig);

      if (this.designSaveListener != null) {
        this.designSaveListener.stop();
        this.designSaveListener = null;
      }

      this._updateSettings({
        process: {
          ...this.settings.process,
          view: AppConstants.PREVIEW_VIEW_PROCESSING,
        },
      });
    }
  },

  _onActiveContainerChange() {
    const blockContainer = ActiveContainer.getContainer();
    const selectedLayout = blockContainer.get("layout");

    if (selectedLayout !== this.settings.selectedLayout) {
      this._updateSettings({ selectedLayout });
    }
  },

  /**
   *
   * @param {*} site
   * @param {LambdaConfig} lambdaConfig
   */
  _generatePreview(site, lambdaConfig) {
    this.generatingPreview = true;

    const siteObj = site.toJS(); // convert from immutable

    SiteService.generatePreview(siteObj, lambdaConfig)
      .then(() => SiteActions.previewComplete())
      .catch(this._onPreviewFail.bind(this));
  },

  onGenerateBlogPreview(blogId, newTab) {
    this.generatingPreview = true;
    this._updateSettings({
      process: {
        ...this.settings.process,
        view: AppConstants.PREVIEW_VIEW_PROCESSING,
      },
    });

    const design = getSiteState();
    const payload = {
      blogId,
      designId: design.get("site_design_id"),
    };
    // Both endpoints return { blogPreviewUrl }.
    const request = this.settings.useProtorenderer
      ? SiteService.generateBlogPreviewV2(payload)
      : SiteService.generateBlogPreview(payload);
    return request
      .then(({ blogPreviewUrl }) => {
        if (newTab) {
          // eslint-disable-next-line no-param-reassign
          newTab.location.href = blogPreviewUrl;
        }
        this.settings = { ...this.settings, blogPreviewUrl };
        SiteActions.previewComplete();
      })
      .catch((err) => {
        newTab?.close();
        this._onPreviewFail(err);
      });
  },

  onGenerateBlogPreviewComplete({ blogPreviewUrl }) {
    this.settings = {
      ...this.settings,
      previewURL: blogPreviewUrl,
      pageURL: blogPreviewUrl,
    };
    SiteActions.previewComplete();
  },

  /**
   * Returns true if lambda config is configured for dev preview
   * @param {LambdaConfig} lambdaConfig
   * @returns {boolean}
   */
  _isDevPreview(lambdaConfig = this.lambdaConfig ?? {}) {
    const { protorenderer, ...rest } = lambdaConfig;
    return !!Object.keys(rest).length && protorenderer === undefined;
  },

  /**
   *
   * @param {BlockContainer} blockContainer
   * @param {ImmutableSiteState} site
   * @returns {{ previewURL: string, pageURL: string }}
   */
  _getPreviewAndPageURL(blockContainer, site) {
    const { devMode, previewDomain, awsURL, previewBucket } = AppConfig;
    const { lambdaConfig } = this;
    const isDevPreview = this._isDevPreview();

    const isCanvasSet = blockContainer.get("isCanvasSet");
    const slug = !isCanvasSet
      ? blockContainer.get("slug")
      : blockContainer.getIn(["currentPageItem", "slug"], "");
    const designKey = site.get("key");
    const subdomain = site.get("subdomain", designKey);
    const pageKey = `${designKey}/${slug}.html`;
    const basePreviewURL = previewDomain + (slug === "index" ? "" : `/${slug}`);

    const previewURL = `${window.location.protocol}//${
      isDevPreview
        ? `${site.get("key")}--pw--${lambdaConfig.previewId}.${basePreviewURL}`
        : `${subdomain}${lambdaConfig?.protorenderer ? ".v2" : ""}.${basePreviewURL}`
    }`;

    const pageURL =
      devMode && !isDevPreview
        ? `${awsURL}/${previewBucket}/${pageKey}?t=${Date.now()}`
        : lambdaConfig?.protorenderer
          ? `${previewURL}?t=${Date.now()}`
          : previewURL;

    return { previewURL, pageURL };
  },

  /**
   * Configures preview params and sets modal state to open.
   * @param {LambdaConfig=} lambdaConfig
   * @param {boolean=} force - force regenerate preview
   */
  onPreviewOpen(lambdaConfig = {}, force) {
    const blockContainer = ActiveContainer.getContainer();
    const site = getSiteState();
    this.lambdaConfig = lambdaConfig;

    const useProtorenderer = lambdaConfig?.protorenderer ?? getSiteData()?.renderer === 2;

    if (lambdaConfig?.protorenderer === undefined) {
      this.lambdaConfig.protorenderer = useProtorenderer;
    }

    const isDevPreview = this._isDevPreview();
    const selectedLayout = blockContainer.get("layout");

    if (!this.activeContainerListener) {
      this.activeContainerListener = this.listenTo(
        ActiveContainer,
        this._onActiveContainerChange,
      );
    }

    const { previewURL, pageURL } = this._getPreviewAndPageURL(
      blockContainer,
      site,
    );

    let view = AppConstants.PREVIEW_VIEW_PROCESSING;

    if (!this.generatingPreview) {
      this.canGeneratePreview = DesignSaveStore.getState();

      if (this.canGeneratePreview) {
        const lastSyncStatusChange = DesignSaveStore.getLastStatusChange();
        // if there has been a change to the design since last time generate preview
        if (
          lastSyncStatusChange !== this.lastSyncStatusChange ||
          force ||
          isDevPreview
        ) {
          this.lastSyncStatusChange = lastSyncStatusChange;
          this._generatePreview(site, lambdaConfig);
        } else {
          // show preview if the design hasn't changed and the user is just opening and closing the window
          view = AppConstants.PREVIEW_VIEW_COMPLETE;
        }
      } else {
        view = AppConstants.PREVIEW_VIEW_SAVING;
        // todo: I think this is a memory leak if reassigned without unsubscribing
        this.designSaveListener = this.listenTo(
          DesignSaveStore,
          // pass through lambda config if site is still saving
          (inSync) => this._onSaveStateChange(inSync, lambdaConfig),
        );
        DesignSaveStore.onSaveSite(); // force the design to save now.
      }
    }

    const initState = {
      process: {
        view,
        error: false,
      },
      selectedLayout,
      previewURL,
      pageURL,
      blogPreviewUrl:
        view === AppConstants.PREVIEW_VIEW_COMPLETE
          ? this.settings.blogPreviewUrl
          : null,
      useProtorenderer,
      lastPreviewed: this.settings.lastPreviewed,
    };

    this.settings = initState;

    this.previewOpen = true;
    UIActions.modalOpen(AppConstants.MODAL_DESIGN_PREVIEW);
  },

  onPreviewClose() {
    this.previewOpen = false;

    if (this.designSaveListener != null) {
      this.designSaveListener.stop();
      this.designSaveListener = null;
    }

    if (this.activeContainerListener != null) {
      this.activeContainerListener.stop();
      this.activeContainerListener = null;
    }

    this.lambdaConfig = {};

    UIActions.modalClose(AppConstants.MODAL_DESIGN_PREVIEW);
  },

  onPreviewComplete() {
    this.generatingPreview = false;
    const previewDate = new Date();

    const lastPreviewed = {
      [V1]: this.settings.useProtorenderer
        ? this.settings?.lastPreviewed?.[V1]
        : previewDate,
      [V2]: previewDate,
    };

    this._updateSettings({
      process: {
        ...this.settings.process,
        view: AppConstants.PREVIEW_VIEW_COMPLETE,
      },
      lastPreviewed,
    });

    queryClient.invalidateQueries({
      queryKey: [RENDERER_LOCK_ELIGIBILITY, getSiteId()],
    });
  },

  // todo: type this
  _onPreviewFail(err) {
    this.generatingPreview = false;

    if (APIError.handle(err)) {
      return;
    }

    const error = ErrorUtils.getFormattedError(err);

    this._updateSettings({
      process: {
        ...this.settings.process,
        view: AppConstants.PREVIEW_VIEW_ERROR,
        error,
      },
    });
  },

  /** @returns {PreviewSettings} */
  getState() {
    return this.settings;
  },

  /** @returns {PreviewSettings} */
  getInitialState() {
    return this.settings;
  },

  /**
   * Toggles protorenderer usage state and updates the page and preview url.
   * @todo remove this eventually
   * @param {boolean} useProtorenderer
   */
  onToggleProtorenderer(useProtorenderer) {
    const site = getSiteState();
    const blockContainer = ActiveContainer.getContainer();
    this.lambdaConfig.protorenderer = useProtorenderer;
    const { lastPreviewed } = this.settings;

    const { previewURL, pageURL } = this._getPreviewAndPageURL(
      blockContainer,
      site,
    );

    const baseUpdateSettings = {
      useProtorenderer,
      // Invalidate the cached blog preview so the next click regenerates against
      // the now-active renderer instead of reopening the other renderer's URL.
      blogPreviewUrl: null,
      pageURL,
      previewURL,
    };

    if (lastPreviewed && lastPreviewed[V2] !== lastPreviewed[V1]) {
      this._updateSettings({
        ...baseUpdateSettings,
        process: { view: AppConstants.PREVIEW_VIEW_PROCESSING },
      });
      this.onPreviewOpen({ protorenderer: useProtorenderer }, true);
    } else {
      this._updateSettings(baseUpdateSettings);
    }
  },
});

export default PreviewStore;
